/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';

import { analytics } from '../analytics';
import { PlatformName } from '../analytics/types';

interface ChunkLoadError {
  error: Error;
  componentName: string;
  loadId: string;
  loadTime: number;
  attempt: number;
  maxAttempts: number;
}

export const logChunkLoadStart = (componentName: string, loadId: string) => {
  Sentry.addBreadcrumb({
    message: `🔄 Starting chunk load for ${componentName}`,
    category: 'chunk_loading',
    data: {
      component: componentName,
      loadId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connectionType:
        (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
          ?.effectiveType || 'unknown',
      isOnline: navigator.onLine,
      url: window.location.href,
    },
    level: 'info',
  });
};

export const logChunkLoadError = ({
  error,
  componentName,
  loadId,
  loadTime,
  attempt,
  maxAttempts,
}: ChunkLoadError) => {
  console.error(`❌ Chunk load error (${attempt}/${maxAttempts}):`, error);

  // Детальное логирование ошибки в Sentry
  Sentry.captureException(error, {
    tags: {
      feature: 'lazy_loading',
      component: componentName,
      error_type: error?.name || 'UnknownError',
      load_phase: 'chunk_import',
      attempt: attempt.toString(),
      max_attempts: maxAttempts.toString(),
      is_final_attempt: (attempt === maxAttempts).toString(),
    },
    extra: {
      component: componentName,
      loadId,
      loadTime: `${loadTime.toFixed(2)}ms`,
      attempt,
      maxAttempts,
      errorMessage: error?.message,
      errorStack: error?.stack,
      errorCode: (error as any)?.code,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connectionType:
        (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
          ?.effectiveType || 'unknown',
      isOnline: navigator.onLine,
      url: window.location.href,
      referrer: document.referrer,
      memoryUsage: (
        performance as Performance & {
          memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
        }
      ).memory
        ? {
            used: Math.round(
              (
                performance as Performance & {
                  memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
                }
              ).memory!.usedJSHeapSize /
                1024 /
                1024,
            ),
            total: Math.round(
              (
                performance as Performance & {
                  memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
                }
              ).memory!.totalJSHeapSize /
                1024 /
                1024,
            ),
          }
        : 'unavailable',
      networkInformation: (
        navigator as Navigator & {
          connection?: { effectiveType?: string; downlink?: number; rtt?: number };
        }
      ).connection
        ? {
            effectiveType: (
              navigator as Navigator & {
                connection?: { effectiveType?: string; downlink?: number; rtt?: number };
              }
            ).connection!.effectiveType,
            downlink: (
              navigator as Navigator & {
                connection?: { effectiveType?: string; downlink?: number; rtt?: number };
              }
            ).connection!.downlink,
            rtt: (
              navigator as Navigator & {
                connection?: { effectiveType?: string; downlink?: number; rtt?: number };
              }
            ).connection!.rtt,
          }
        : 'unavailable',
    },
    level: 'error',
  });

  // Отправляем аналитику ошибки (только поддерживаемые поля)
  analytics.emitEvent('service_error_system', {
    [PlatformName.GTM]: {
      action: attempt === maxAttempts ? 'lazy_import_failed_final' : 'lazy_import_failed_retry',
      message: `${error.message} (attempt ${attempt}/${maxAttempts})`,
    },
  });
};
